/* eslint-disable @typescript-eslint/indent */
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { generatePath, useLocation, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { Field, Form, Formik } from 'formik';
import { Loader, Tooltip } from '@sdir/sds';
import styled from 'styled-components';
import { AttachmentResult, AttachmentType } from '@sdir/httpclient/lib/clients/aps/application';
import { FileLinkItem, FormFileUpload } from '@sdir/blueprint.aps/lib/components';
import NewApplicationLayout from '../../NewApplicationLayout';
import { routes } from '../../../../constants/routes';
import { Card, InputError } from '../../../Atoms';
import { SubmitSequenceSummary } from '../../../Molecules';
import { useCurrentApplication, useSubmitSequence } from '../../../../services/hooks';
import {
  QUALIFICATION_URI,
  findQualification,
  getFromStorage,
} from '../../../../helpers/Qualifications';
import {
  buildCreateAttachmentGroupSequence,
  buildSubmitAttachmentUpdatesSequence,
  CreateAttachmentGroupTracker,
  downloadApplicationFile,
  SubmitAttachmentUpdatesParams,
  SubmitAttachmentUpdatesTracker,
} from '../../../../helpers/attachmentApiHelpers';
import { getTextFromPotentialHtmlString } from '../../../../helpers/formattingHelpers';
import EditApplicationGuard from '../../../Molecules/EditApplicationGuard/EditApplicationGuard';
import { FileViewer } from '../../../Molecules/FileDrop/FileViewer';
import { useNavigateWithLang } from '../../../../services/hooks/useNavigateWithLang';

export const AddPhoto: FC = () => {
  const intl = useIntl();
  const navigate = useNavigateWithLang();
  const [activeFile, setActiveFile] = useState<File | null>();
  const { attachmentGroup, applicationUid } = useParams<{
    attachmentGroup: string;
    applicationUid: string;
  }>();
  const attachmentGroupFormatted = attachmentGroup === 'new' ? null : attachmentGroup || null;
  const uriFromStorage = getFromStorage(QUALIFICATION_URI);

  const { application, refresh, qualifications, loadQualifications } = useCurrentApplication({
    includeQualifications: true,
  });

  const attachmentsForGroup = useMemo(
    () =>
      application.result?.attachments?.filter((a) => a.group === attachmentGroupFormatted) || [],
    [application.result?.attachments, attachmentGroupFormatted]
  );

  const processInitialFile = async (uid: string, attachment: AttachmentResult) => {
    const file = await downloadApplicationFile(uid, attachment);
    if (file) setActiveFile(file);
  };

  useEffect(() => {
    const att = attachmentsForGroup?.[0];
    if (att && applicationUid) {
      processInitialFile(applicationUid, att);
    }
  }, [attachmentsForGroup, applicationUid]);

  const groupQualificationUri =
    attachmentsForGroup?.[0]?.simulatorResultItemUri || uriFromStorage || null;

  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const fromMissingDocumentation = searchParams.get('from') === 'missingdocumentation';

  const initialValues = useMemo((): any => {
    return {
      attachment: activeFile || null,
    };
  }, [activeFile]);

  const {
    isSubmitting,
    results,
    submit: submitAttachments,
  } = useSubmitSequence<SubmitAttachmentUpdatesParams, SubmitAttachmentUpdatesTracker>(
    buildSubmitAttachmentUpdatesSequence
  );
  const { submit: submitAttachmentGroup } = useSubmitSequence(buildCreateAttachmentGroupSequence);

  const [showValidation, setShowValidation] = useState(false);

  const [submitAttachmentsTracker, setSubmitAttachmentsTracker] =
    useState<SubmitAttachmentUpdatesTracker>({});
  const [submitAttachmentGroupTracker, setSubmitAttachmentGroupTracker] =
    useState<CreateAttachmentGroupTracker>({});

  const qualification = useMemo(() => {
    if (!qualifications.result || !groupQualificationUri) return null;
    return findQualification(qualifications.result, groupQualificationUri);
  }, [qualifications.result, groupQualificationUri]);

  const handleActiveFileChange = useCallback(
    (files: File[] | null) => {
      const file = files?.[0];
      if (file !== activeFile) {
        setActiveFile(file);
      }
    },
    [activeFile]
  );

  const handleExit = useCallback(() => {
    const route = fromMissingDocumentation
      ? routes.application.uploadMissingDocumentation
      : routes.application.qualification;
    navigate(generatePath(route, { applicationUid }));
  }, [navigate, applicationUid, fromMissingDocumentation]);

  const hasGroupFiles = !!attachmentsForGroup.length;

  const handleSubmit = useCallback(
    async (values) => {
      if (!application.result) {
        setShowValidation(true);
        return;
      }

      let newAttachmentTracker: SubmitAttachmentUpdatesTracker | null = null;
      let newAttachmentGroupTracker: CreateAttachmentGroupTracker | null = null;
      let group = attachmentGroupFormatted;
      if (!group) {
        // Create new attachment group
        const { tracker: attachmentGroupTracker } = await submitAttachmentGroup(
          {
            application: application.result,
            attachmentType: AttachmentType.Photo,
            files: values.attachment ? [values.attachment] : [],
            simulatorResultItemUri: groupQualificationUri,
          },
          submitAttachmentGroupTracker
        );
        group =
          Object.values(attachmentGroupTracker?.addedAttachments || {})[0]?.result?.group || null;
        newAttachmentGroupTracker = attachmentGroupTracker;
      } else {
        const existingCropUid = application?.result?.attachments?.find(
          (a) => a.type === AttachmentType.PhotoCropped
        )?.uid;
        // Submit attachments, when we already have an attachment group
        const { tracker: attachmentTracker } = await submitAttachments(
          {
            application: application.result,
            attachmentGroup: attachmentGroupFormatted,
            qualificationUri: groupQualificationUri,
            updatedAttachments: [],
            removedAttachments: values?.attachment
              ? (attachmentsForGroup || [])
                  .map((a) => a?.uid || '')
                  .concat(existingCropUid ? [existingCropUid] : [])
              : [],
            newAttachments: values?.attachment
              ? [{ file: values.attachment, type: AttachmentType.Photo }]
              : [],
          },
          submitAttachmentsTracker
        );
        group = attachmentTracker?.newAttachments?.[0]?.result?.group || null;
        newAttachmentTracker = attachmentTracker;
      }

      if (newAttachmentTracker?.errors || newAttachmentGroupTracker?.errors) {
        if (newAttachmentTracker) setSubmitAttachmentsTracker(newAttachmentTracker);
        if (newAttachmentGroupTracker) setSubmitAttachmentGroupTracker(newAttachmentGroupTracker);
      } else {
        refresh?.();
        loadQualifications?.();
        handleExit();
      }
    },
    [
      application.result,
      attachmentGroupFormatted,
      attachmentsForGroup,
      groupQualificationUri,
      handleExit,
      refresh,
      loadQualifications,
      submitAttachments,
      submitAttachmentGroup,
      submitAttachmentsTracker,
      submitAttachmentGroupTracker,
    ]
  );

  const validation = useMemo(
    () =>
      Yup.object().shape({
        attachment: Yup.mixed()
          .test('has-files', intl.formatMessage({ id: 'general.form.error' }), (v: any) => {
            if (!v) return attachmentsForGroup.length > 0;
            return !!v;
          })
          .test('file-size', intl.formatMessage({ id: 'addPhoto.filesize' }), () => {
            // Maximum 10MB file size
            const tooLargeFile = (activeFile?.size || 0) > 1000 * 1000 * 10;
            return !tooLargeFile;
          }),
      }),
    [attachmentsForGroup.length, activeFile, intl]
  );

  const formattedDescription = useMemo(() => {
    return getTextFromPotentialHtmlString(qualification?.description);
  }, [qualification?.description]);

  const heading = qualification
    ? `${qualification.title}: ${formattedDescription}`
    : intl.formatMessage({ id: 'addPhoto.heading' });

  return (
    <EditApplicationGuard>
      {application.loading ? (
        <Loader />
      ) : (
        <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={validation}>
          {({ submitForm }) => (
            <Form noValidate>
              <NewApplicationLayout
                exitLabel={intl.formatMessage({ id: 'general.form.back' })}
                mainContentMaxWidth="100%"
                canNavBack
                canNavNext={!isSubmitting}
                navBackLabel={intl.formatMessage({ id: 'general.form.goback' })}
                navNextLabel={intl.formatMessage({ id: 'general.form.next' })}
                onNavNextClick={submitForm}
                onNavBackClick={handleExit}
              >
                <PhotoFormContent>
                  <div>
                    <Card heading={heading}>
                      <ul>
                        <li>{intl.formatMessage({ id: 'addPhoto.instruction1' })}</li>
                        <li>{intl.formatMessage({ id: 'addPhoto.instruction2' })}</li>
                        <li>{intl.formatMessage({ id: 'addPhoto.instruction3' })}</li>
                        <li>{intl.formatMessage({ id: 'addPhoto.instruction4' })}</li>
                        <li>{intl.formatMessage({ id: 'addPhoto.instruction5' })}</li>
                        <li>{intl.formatMessage({ id: 'addPhoto.instruction6' })}</li>
                        <li>
                          <LiContent>
                            <span>{intl.formatMessage({ id: 'addPhoto.instruction7' })}</span>
                            <Tooltip small>
                              {intl.formatMessage({ id: 'addPhoto.instruction7.tooltip' })}
                            </Tooltip>
                          </LiContent>
                        </li>
                        <li>{intl.formatMessage({ id: 'addPhoto.instruction8' })}</li>
                      </ul>
                      <p>{intl.formatMessage({ id: 'addPhoto.imageformat' })}</p>
                      <Field
                        className="file-input"
                        name="attachment"
                        component={FormFileUpload}
                        allowedTypes=".png,.jpg,.jpeg"
                        label={intl.formatMessage({ id: 'addPhoto.upload' })}
                        onCallback={handleActiveFileChange}
                      />
                      {activeFile && (
                        <FileLinkItem
                          file={activeFile}
                          isSelected
                          index={0}
                          handleChange={() => {}}
                        />
                      )}
                    </Card>
                  </div>
                  <RightSection>
                    {activeFile && <FileViewer file={activeFile} />}
                    {!hasGroupFiles && showValidation && (
                      <InputError>{intl.formatMessage({ id: 'general.form.error' })}</InputError>
                    )}
                  </RightSection>
                </PhotoFormContent>
                {isSubmitting && <Loader />}
                <SubmitSequenceSummary results={results} />
              </NewApplicationLayout>
            </Form>
          )}
        </Formik>
      )}
    </EditApplicationGuard>
  );
};

const PhotoFormContent = styled.div`
  display: grid;
  grid-template-rows: auto auto;
  grid-row-gap: 3rem;
  grid-template-columns: 1fr 1fr;
  grid-column-gap: 32px;
  margin-top: 3rem;
  justify-content: stretch;

  > * {
    position: relative;
    min-height: calc(100vh - 400px);
  }

  @media screen and (max-width: ${({ theme }) => theme.screen.medium}) {
    grid-template-columns: 1fr;

    > div:first-child {
      min-height: 0;
    }

    > div:nth-child(2) {
      min-height: 500px;
    }
  }
`;

const LiContent = styled.div`
  display: flex;
  flex-direction: row;
  span {
    flex: 1;
    margin-right: 5px;
  }
`;

const RightSection = styled.div`
  max-height: 1000px;
  overflow: auto;
`;
